/// <amd-module name="ServiceHooks/Web/Admin/Components/HistoryTable/HistoryTable" />
import * as React from "react";
import { Notification, NotificationResult } from "ServiceHooks/Web/Admin/Contracts/ServiceHooks";
import * as Resources from "ServiceHooks/Web/Admin/Resources";
import { getNotificationStatusBundle, getTextForNotificationResult } from "Utilities";
import { IReadonlyObservableValue, ObservableValue, useObservableArray } from "VSS/Core/Observable";
import { Card } from "VSSUI/Card";
import { Status, StatusSize } from "VSSUI/Status";
import { ColumnSorting, ITableColumn, SimpleTableCell, sortItems, SortOrder, Table, TableColumnLayout } from "VSSUI/Table";
import { Tooltip } from "VSSUI/TooltipEx";
import { FILTER_CHANGE_EVENT } from "VSSUI/Utilities/Filter";
import { HistoryTableFilterIds, HistoryTableItem, IHistoryTableProps } from "./HistoryTable.Props";

function renderStatusColumn(rowIndex: number, columnIndex: number, tableColumn: ITableColumn<Notification>, tableItem: Notification) {
    const statusBundle = getNotificationStatusBundle(tableItem);

    return (
        <SimpleTableCell className="flex-row" columnIndex={columnIndex} key={columnIndex} tableColumn={tableColumn}>
            <Status {...statusBundle.statusProps} className="icon-margin" size={StatusSize.s} />
            <Tooltip overflowOnly={true}>
                <div className="text-ellipsis">{statusBundle.label}</div>
            </Tooltip>
        </SimpleTableCell>
    );
}

function renderCreatedDateColumn(rowIndex: number, columnIndex: number, tableColumn: ITableColumn<Notification>, tableItem: Notification) {
    return (
        <SimpleTableCell className="flex-row" columnIndex={columnIndex} key={columnIndex} tableColumn={tableColumn}>
            <Tooltip overflowOnly={true}>
                <div className="text-ellipsis">{tableItem.createdDate.toLocaleString()}</div>
            </Tooltip>
        </SimpleTableCell>
    );
}

const onSize = (event: MouseEvent, index: number, width: number) => {
    (historyTableColumns[index].width as ObservableValue<number>).value = width;
};

const historyTableColumns: ITableColumn<Notification>[] = [
    {
        id: "date",
        name: Resources.HistoryTableSentAtHeader,
        onSize: onSize,
        renderCell: renderCreatedDateColumn,
        width: new ObservableValue(-50),
        columnLayout: TableColumnLayout.singleLine
    },
    {
        id: "status",
        name: Resources.HistoryTableStatusHeader,
        onSize: onSize,
        renderCell: renderStatusColumn,
        width: new ObservableValue(-50),
        columnLayout: TableColumnLayout.singleLinePrefix
    }
];

function isObservable(item: HistoryTableItem): item is IReadonlyObservableValue<Notification | undefined> {
    return (item as IReadonlyObservableValue<Notification | undefined>).subscribe !== undefined;
}

function getTableItemAsNotification(item: HistoryTableItem): Notification | undefined {
    if (isObservable(item)) {
        return (item as IReadonlyObservableValue<Notification | undefined>).value;
    } else {
        return item as Notification;
    }
}

function compareOnValue(itemOne: HistoryTableItem, itemTwo: HistoryTableItem, getValue: (Notification: Notification) => string): number;
function compareOnValue(itemOne: HistoryTableItem, itemTwo: HistoryTableItem, getValue: (Notification: Notification) => number): number;
function compareOnValue(itemOne: HistoryTableItem, itemTwo: HistoryTableItem, getValue: (notification: Notification) => string | number): number {
    let itemOneValue: string | number = 0;
    let itemTwoValue: string | number = 0;

    const itemOneAsNotification = getTableItemAsNotification(itemOne);
    const itemTwoAsNotification = getTableItemAsNotification(itemTwo);

    // Parse out values; if one is undefined, say the other comes first
    // If both undefined, they're equivalent
    if (itemOneAsNotification && itemTwoAsNotification) {
        itemOneValue = getValue(itemOneAsNotification);
        itemTwoValue = getValue(itemTwoAsNotification);
    } else if (itemOneAsNotification) {
        return 1;
    } else if (itemTwoAsNotification) {
        return -1;
    } else {
        return 0;
    }

    if (typeof itemOneValue === "number" && typeof itemTwoValue === "number") {
        return itemTwoValue - itemOneValue;
    } else if (typeof itemOneValue === "string" && typeof itemTwoValue === "string") {
        return itemOneValue.localeCompare(itemTwoValue, navigator.language);
    } else {
        // If they don't reduce to the same type, we've hit a weird snag that type guards above should prevent
        return 0;
    }
}

const historyTableSortFunctions = [
    // Sent At
    (itemOne: HistoryTableItem, itemTwo: HistoryTableItem): number => {
        return compareOnValue(itemOne, itemTwo, (item: Notification) => item.createdDate.getTime());
    },
    // Status
    (itemOne: HistoryTableItem, itemTwo: HistoryTableItem): number => {
        return compareOnValue(itemOne, itemTwo, (item: Notification) => getTextForNotificationResult(item.result));
    }
];

export const HistoryTable: React.FunctionComponent<IHistoryTableProps> = props => {
    const { filter, notifications, onRowActivated } = props;

    const [filteredItems, setFilteredItems] = useObservableArray([...notifications.value]);

    const [sortingBehavior] = React.useState(
        () =>
            new ColumnSorting<HistoryTableItem>((columnIndex: number, proposedSortOrder: SortOrder) => {
                filteredItems.splice(
                    0,
                    filteredItems.length,
                    ...sortItems<HistoryTableItem>(
                        columnIndex,
                        proposedSortOrder,
                        historyTableSortFunctions,
                        historyTableColumns,
                        filteredItems.value
                    )
                );
            })
    );

    React.useEffect(() => {
        filter.subscribe(updateFilteredItems, FILTER_CHANGE_EVENT);
        notifications.subscribe(updateFilteredItems);

        return () => {
            filter.unsubscribe(updateFilteredItems, FILTER_CHANGE_EVENT);
            notifications.unsubscribe(updateFilteredItems);
        };
    }, []);

    const updateFilteredItems = () => {
        const filterResult = filter.getFilterItemValue<NotificationResult[]>(HistoryTableFilterIds.STATUS);
        const filterText = filter.getFilterItemValue<String>(HistoryTableFilterIds.TEXT);

        const isLegalItem = (item: Notification | IReadonlyObservableValue<Notification | undefined>) => {
            if ("result" in item) {
                if (filterResult && filterResult.length > 0 && !filterResult.some(testItem => item.result === testItem)) {
                    return false;
                }

                if (filterText) {
                    const filterSplit = filterText.toLocaleLowerCase().split(" ");

                    const testItemAsString = [item.createdDate.toLocaleString(), getTextForNotificationResult(item.result)]
                        .join(" ")
                        .toLocaleLowerCase();

                    if (!filterSplit.every(term => testItemAsString.indexOf(term) !== -1)) {
                        return false;
                    }
                }
            }

            return true;
        };

        setFilteredItems(notifications.value.filter(isLegalItem));
    };

    return (
        <Card className={"history-table bolt-table-card"} contentProps={{ contentPadding: false }}>
            <Table
                behaviors={[sortingBehavior]}
                columns={historyTableColumns}
                itemProvider={filteredItems}
                onActivate={(event, item) => {
                    const itemAsNotification = getTableItemAsNotification(item.data);
                    if (itemAsNotification) {
                        onRowActivated(itemAsNotification);
                    }
                }}
            />
        </Card>
    );
};
